import * as React from 'react';

import PropTypes from 'prop-types';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import createLoadableComponent from 'js/lib/createLoadableComponent';

import type { PropsFromWithProductFeatures } from 'bundles/admin-dashboard/components/AdminDashboardPage';
import type { ThirdPartyOrganization } from 'bundles/admin-dashboard/types';
import {
  COURSERA_FOR_CAMPUS_BASIC_SETUP_PATH,
  FREEMIUM_INVITE_LEARNERS_PATH,
  FREEMIUM_PROGRAM_SETUP_PATH,
  FREEMIUM_PROGRAM_SURVEY_PATH,
} from 'bundles/enterprise-admin-coursera-for-campus-basic/constants';
import { handleRedirect } from 'bundles/enterprise-admin-coursera-for-campus-basic/utils/utils';

const CourseraForCampusBasicSetupContainer = createLoadableComponent(
  () => import('bundles/enterprise-admin-coursera-for-campus-basic/components/CourseraForCampusBasicSetupContainer')
);
const ProgramSurveyContainer = createLoadableComponent(
  // FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
  // eslint-disable-next-line import/no-cycle
  () => import('bundles/enterprise-admin-coursera-for-campus-basic/components/ProgramSurveyContainer')
);
const InviteLearnersFlowContainer = createLoadableComponent(
  () => import('bundles/enterprise-admin-coursera-for-campus-basic/components/InviteLearnersFlowContainer')
);

type SecondaryOrgTypeProps = Partial<Pick<PropsFromWithProductFeatures, 'isC4CBOrg' | 'isGwGOrg' | 'isGwGFreemiumOrg'>>;

export type Props = {
  contracts: Array<{ id: string; contractState: string }>;
  thirdPartyOrganization: ThirdPartyOrganization;
} & SecondaryOrgTypeProps;

const withCourseraForCampusBasicSetup = (Component: React.ComponentType) =>
  class CourseraForCampusBasicSetup extends React.Component<Props> {
    static contextTypes = {
      router: PropTypes.object.isRequired,
    };

    declare context: {
      router: InjectedRouter;
    };

    render() {
      const { router } = this.context;
      const { thirdPartySlug, programSlug } = router.params;
      const { contracts = [], thirdPartyOrganization, isC4CBOrg } = this.props;
      const currentPath = router.routes[router.routes.length - 1].path;
      const isCourseraForCampusBasicSetupPath = currentPath === COURSERA_FOR_CAMPUS_BASIC_SETUP_PATH;
      const isFreemiumProgramSetupPath = currentPath === FREEMIUM_PROGRAM_SETUP_PATH;
      const isFreemiumInviteLearnersPath = currentPath === FREEMIUM_INVITE_LEARNERS_PATH;
      const isProgramSurveyPath = currentPath === FREEMIUM_PROGRAM_SURVEY_PATH;

      if (
        isCourseraForCampusBasicSetupPath ||
        isFreemiumProgramSetupPath ||
        isFreemiumInviteLearnersPath ||
        isProgramSurveyPath
      ) {
        if (!isC4CBOrg) {
          handleRedirect(`/o/${thirdPartySlug}/admin/home`, router);
          return null;
        }

        if (isFreemiumProgramSetupPath) {
          return (
            <CourseraForCampusBasicSetupContainer
              contracts={contracts}
              orgSlug={thirdPartySlug}
              orgId={thirdPartyOrganization.id}
              programSlug={programSlug}
              router={router}
              isProgramCreation
            />
          );
        }

        if (isCourseraForCampusBasicSetupPath) {
          return (
            <CourseraForCampusBasicSetupContainer
              contracts={contracts}
              orgSlug={thirdPartySlug}
              orgId={thirdPartyOrganization.id}
              router={router}
              programSlug={programSlug}
            />
          );
        }

        if (isFreemiumInviteLearnersPath) {
          return <InviteLearnersFlowContainer orgSlug={thirdPartySlug} orgId={thirdPartyOrganization.id} />;
        }

        if (isProgramSurveyPath) {
          return <ProgramSurveyContainer />;
        }
      }

      // @ts-expect-error TS2559: Type '{ contracts: EnterpriseContractsV1[]; thirdPartyOrganization: AdminDashboardPageBaseQuery_ThirdPartyOrganizationsV1Resource_bySlug_elements; isC4CBOrg?: boolean | undefined; isGwGOrg?: boolean | undefined; isGwGFreemiumOrg?: boolean | undefined; }' has no properties in common with type 'IntrinsicAttributes'.
      return <Component {...this.props} />;
    }
  };

export default withCourseraForCampusBasicSetup;
